import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useHaptic } from '../../hooks/useHaptic';

export type AdminGlassAccent = 'cyan' | 'emerald' | 'violet' | 'amber' | 'coral' | 'slate';

const ACCENT: Record<
  AdminGlassAccent,
  { bar: string; ring: string; headerTint: string; title: string }
> = {
  cyan: {
    bar: 'linear-gradient(180deg, #63B3ED 0%, #3182CE 50%, #2B6CB0 100%)',
    ring: 'rgba(99,179,237,0.22)',
    headerTint: 'rgba(99,179,237,0.06)',
    title: '#63B3ED',
  },
  emerald: {
    bar: 'linear-gradient(180deg, #68D391 0%, #38A169 50%, #276749 100%)',
    ring: 'rgba(72,187,120,0.22)',
    headerTint: 'rgba(72,187,120,0.06)',
    title: '#68D391',
  },
  violet: {
    bar: 'linear-gradient(180deg, #D6BCFA 0%, #9F7AEA 50%, #805AD5 100%)',
    ring: 'rgba(159,122,234,0.25)',
    headerTint: 'rgba(159,122,234,0.08)',
    title: '#D6BCFA',
  },
  amber: {
    bar: 'linear-gradient(180deg, #F6AD55 0%, #ED8936 50%, #DD6B20 100%)',
    ring: 'rgba(246,173,85,0.22)',
    headerTint: 'rgba(246,173,85,0.07)',
    title: '#F6AD55',
  },
  coral: {
    bar: 'linear-gradient(180deg, #FCA58D 0%, #FC814A 50%, #C05621 100%)',
    ring: 'rgba(252,129,74,0.22)',
    headerTint: 'rgba(252,129,74,0.06)',
    title: '#FC814A',
  },
  slate: {
    bar: 'linear-gradient(180deg, #CBD5E1 0%, #94A3B8 50%, #64748B 100%)',
    ring: 'rgba(148,163,184,0.2)',
    headerTint: 'rgba(255,255,255,0.04)',
    title: 'var(--text-2)',
  },
};

export type AdminGlassDisclosureProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  icon?: ReactNode;
  accent?: AdminGlassAccent;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /** Развернуть при появлении ошибки/предупреждения (например после загрузки) */
  expandSignal?: boolean;
  density?: 'comfortable' | 'compact';
  /** Справа от заголовка, перед шевроном (бейджи ошибок, быстрые индикаторы) */
  headerRight?: ReactNode;
};

/**
 * Сворачиваемая секция в стиле Liquid Glass: акцентная полоса, мягкое свечение при открытии, плавная анимация высоты.
 */
export function AdminGlassDisclosure({
  title,
  subtitle,
  badge,
  icon,
  accent = 'cyan',
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className = '',
  headerClassName = '',
  contentClassName = '',
  expandSignal,
  density = 'comfortable',
  headerRight,
}: AdminGlassDisclosureProps) {
  const haptic = useHaptic();
  const autoId = useId();
  const contentId = `ad-${autoId}`;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  useEffect(() => {
    if (expandSignal) setOpen(true);
  }, [expandSignal, setOpen]);

  const a = ACCENT[accent];
  const py = density === 'compact' ? 'py-2' : 'py-2.5';

  return (
    <div
      className={`relative rounded-2xl overflow-hidden glass ${className}`.trim()}
      style={{
        border: `1px solid var(--border-subtle)`,
        boxShadow: open ? `0 0 0 1px ${a.ring}, 0 8px 32px rgba(0,0,0,0.12)` : undefined,
        transition: 'box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <div
        className="pointer-events-none absolute left-0 top-3 bottom-3 w-[3px] rounded-full z-[1]"
        style={{ background: a.bar, opacity: open ? 1 : 0.45, transition: 'opacity 0.3s ease' }}
        aria-hidden
      />
      <button
        type="button"
        id={`${contentId}-header`}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => {
          haptic.selection();
          setOpen(!open);
        }}
        className={`relative z-[2] w-full flex items-start gap-2.5 px-3 ${py} pr-3 text-left press ${headerClassName}`.trim()}
        style={{
          background: open ? a.headerTint : 'transparent',
          transition: 'background 0.3s ease',
        }}
      >
        {icon && (
          <span className="shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: a.headerTint, border: `1px solid ${a.ring}` }}>
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: a.title }}>
              {title}
            </span>
            {badge != null && badge !== '' && (
              <span
                className="text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  color: 'var(--text-2)',
                  border: '1px solid var(--border-glass)',
                }}
              >
                {badge}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-[11px] mt-0.5 leading-snug line-clamp-2" style={{ color: 'var(--text-3)' }}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          {headerRight}
          <motion.span
            className="shrink-0"
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            style={{ color: 'var(--text-3)' }}
          >
            <ChevronDown size={18} strokeWidth={2.25} />
          </motion.span>
        </div>
      </button>

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="min-h-0 overflow-hidden">
          <motion.div
            id={contentId}
            role="region"
            aria-labelledby={`${contentId}-header`}
            initial={false}
            animate={{ opacity: open ? 1 : 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={`px-3 pb-3 ${contentClassName}`.trim()}
            style={{ pointerEvents: open ? 'auto' : 'none' }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
